// Moteur BALKAN FamilyTreeJS (licence commerciale — OK pour évaluation) : rendu
// le plus abouti visuellement, export PDF/PNG/SVG NATIF intégré. Données au
// format Balkan { id, pids, mid, fid, name, gender }.
import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import FamilyTree from '@balkangraph/familytree.js';
import { Membre, Relation } from '../../types';
import { toPersonNodes } from '../../utils/genealogyModel';
import { EngineExportHandle } from './engineTypes';

interface Props {
  membres: Membre[];
  relations: Relation[];
  dark?: boolean;
}

const year = (d?: string) => (d ? String(new Date(d).getFullYear()) : '');

const BalkanChart = forwardRef<EngineExportHandle, Props>(function BalkanChart(
  { membres, relations, dark }, ref,
) {
  const hostRef = useRef<HTMLDivElement>(null);
  const treeRef = useRef<FamilyTree | null>(null);

  useImperativeHandle(ref, () => ({
    exportPdf: () => { treeRef.current?.exportPDF(); },
    exportPng: () => { treeRef.current?.exportPNG(); },
  }));

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    // On n'inclut que les clés définies : Balkan tolère mal mid/fid/gender à undefined.
    const nodes = toPersonNodes(membres, relations).map(n => {
      const node: Record<string, unknown> = {
        id: n.id,
        name: `${n.prenom} ${n.nom}`.trim(),
        birthday: year(n.date_naissance),
      };
      if (n.spouses.length) node.pids = n.spouses;
      if (n.mother != null) node.mid = n.mother;
      if (n.father != null) node.fid = n.father;
      if (n.sexe === 'Femme') node.gender = 'female';
      else if (n.sexe === 'Homme') node.gender = 'male';
      return node;
    });
    if (!nodes.length) { host.innerHTML = ''; return; }
    // eslint-disable-next-line no-console
    console.log('[DIAG] Balkan reçoit', nodes.length, 'nœuds ; conteneur', host.clientWidth, 'x', host.clientHeight);
    try {
      treeRef.current = new FamilyTree(host, {
        nodes: nodes as unknown as object[],
        nodeBinding: { field_0: 'name', field_1: 'birthday' },
        mode: dark ? 'dark' : 'light',
        // La recherche Balkan (activée par défaut) plante en embarqué
        // (searchUI.init → addEventListener sur null) et interrompt le rendu.
        enableSearch: false,
        mouseScrool: FamilyTree.action.none,
      });
      // Cadrage APRÈS le layout (scaleInitial:boundary plante en NaN sur ce jeu
      // de données ; fit() post-rendu est plus sûr). Best-effort.
      setTimeout(() => { try { treeRef.current?.fit(); } catch { /* noop */ } }, 300);
    } catch { /* rendu impossible */ }
    return () => {
      try { (treeRef.current as unknown as { destroy?: () => void })?.destroy?.(); } catch { /* noop */ }
      host.innerHTML = '';
      treeRef.current = null;
    };
  }, [membres, relations, dark]);

  return <div ref={hostRef} style={{ width: '100%', height: 460 }} />;
});

export default BalkanChart;
